import { type FC, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import './../FansList/_fansList.scss'

import {
  IconEdit,
  IconThreeDotsVertical,
  IconTwoPeople
} from 'assets/svg/sprite'
import { OptionsDropdown } from 'components-ref/molecules'
import { type IOption } from 'types/iTypes'

const SubscriptionsList: FC<{
  title: string
  fans: {
    avatars: string[]
    count: number
    id?: string
    data?: any
  }
  clickFn?: () => void
  customClass?: string
  onDelete?: (id: string) => void
  onEdit?: (id: string, name: string) => void
  id?: string
  name?: string
  showZeroCount?: boolean
  hideOptions?: boolean
  disableNavigate?: boolean
}> = ({
  title,
  fans,
  clickFn,
  customClass,
  onDelete,
  onEdit,
  name,
  showZeroCount = false,
  hideOptions = false,
  disableNavigate = false
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)

  const listId = fans?.data?.id
  const listName = (fans?.data?.name ?? name ?? title) as string
  const isDefaultGroup = Number(fans?.data?.default) === 1

  const subscriptionsNumber = () => {
    if (!fans) return t('empty')

    const count = Number.isFinite(Number(fans.count)) ? Number(fans.count) : 0

    if (count === 0 && !showZeroCount) return t('empty')

    if (count === 1) {
      return `${count} Subscription`
    }

    if (count < 1000) {
      return `${count} Subscriptions`
    }

    return `+${parseFloat((count / 1000).toFixed(1))}k Subscriptions`
  }

  const optionsArray: IOption[] = useMemo(() => {
    return [
      {
        text: t('renameList'),
        icon: <IconEdit />,
        disabled: false,
        action: (idFromDropdown: string) => {
          onEdit?.(idFromDropdown, listName)
          setIsOptionsNavOpen(false)
        }
      }
    ]
  }, [t, onEdit, listName])

  return (
    <div
      className={`fansList ${customClass ?? ''}`}
      onClick={() => {
        clickFn?.()
      }}
    >
      <div
        className="fansList__left"
        onClick={() => {
          if (!disableNavigate && listId != null) {
            navigate(`/subscriptions/${listId}`, { state: { listName } })
          }
        }}
      >
        <div className="fansList__left-icon">
          <IconTwoPeople />
        </div>

        <div className="fansList__left-text">
          <h2 className="fansList__left__title">{title}</h2>
          <p className="fansList__left__fansNumber">
            {subscriptionsNumber()}
          </p>
        </div>
      </div>

      {!hideOptions && !isDefaultGroup && (
        <div className="fansList__right">
          <button
            type="button"
            className="fansList__optionsBtn"
            aria-label={t('options')}
            onClick={(e) => {
              e.stopPropagation()
              setIsOptionsNavOpen(true)
            }}
          >
            <IconThreeDotsVertical color="grey" />
          </button>

          {isOptionsNavOpen && (
            <OptionsDropdown
              role="owner"
              content="list"
              isOptionsNavOpen={isOptionsNavOpen}
              setIsOptionsNavOpen={setIsOptionsNavOpen}
              postId={listId}
              options={optionsArray}
              deleteFn={() => {
                if (listId != null) onDelete?.(listId)
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default SubscriptionsList